/**
 * Side-by-side CSI Pay / Tipalti comparison for one CSI-Tipalti Analysis row (AB#6872).
 * The server sends no mismatch flags, so the difference is worked out here from the
 * values already loaded.
 *
 * PURE module: no React, MUI, axios or auth imports.
 */
import type { CSITipaltiStaging } from "../../types/CSITypes";

export type CompareKey = "name" | "email" | "address" | "paymentMethod";

/** Shown instead of an empty value. */
export const NONE_ON_FILE = "None on file";

/** The compared fields, in display order. */
export const COMPARE_FIELDS: { key: CompareKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "paymentMethod", label: "Payment method" },
];

export type CompareSource = Pick<
  CSITipaltiStaging,
  | "csiName"
  | "csiEmail"
  | "csiAddress"
  | "csiPaymentMethod"
  | "tipaltiName"
  | "tipaltiEmail"
  | "tipaltiAddress"
  | "tipaltiPaymentMethod"
>;

/**
 * Comparison form of a value: trimmed, lower-cased, and every run of whitespace or
 * punctuation collapsed to one space. "9 Mill Pond Ln., Macon,  GA" and
 * "9 mill pond ln macon ga" compare equal.
 */
export function normalizeForCompare(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9À-ɏ]+/g, " ")
    .trim();
}

/** The value as shown: trimmed, or "None on file" when empty. */
export function displayCompareValue(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? NONE_ON_FILE : trimmed;
}

/** True when the two values differ after normalising; two empty values match. */
export function valuesDiffer(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return normalizeForCompare(a) !== normalizeForCompare(b);
}

const pairFor = (
  row: CompareSource,
  key: CompareKey
): [string | null | undefined, string | null | undefined] => {
  switch (key) {
    case "name":
      return [row.csiName, row.tipaltiName];
    case "email":
      return [row.csiEmail, row.tipaltiEmail];
    case "address":
      return [row.csiAddress, row.tipaltiAddress];
    case "paymentMethod":
      return [row.csiPaymentMethod, row.tipaltiPaymentMethod];
  }
};

export interface FieldComparison {
  key: CompareKey;
  label: string;
  /** CSI Pay value as shown ("None on file" when empty). */
  csi: string;
  /** Tipalti value as shown ("None on file" when empty). */
  tipalti: string;
  mismatch: boolean;
}

/** Name, email, address and payment method for both sides, with mismatches flagged. */
export function compareFields(row: CompareSource): FieldComparison[] {
  return COMPARE_FIELDS.map(({ key, label }) => {
    const [csi, tipalti] = pairFor(row, key);
    return {
      key,
      label,
      csi: displayCompareValue(csi),
      tipalti: displayCompareValue(tipalti),
      mismatch: valuesDiffer(csi, tipalti),
    };
  });
}

/** The keys whose CSI Pay and Tipalti values differ. */
export function mismatchedKeys(row: CompareSource): CompareKey[] {
  return compareFields(row)
    .filter((field) => field.mismatch)
    .map((field) => field.key);
}
